import React, { createContext, useContext, useState, useEffect } from 'react';
import { UI_CONSTANTS } from '../constants/index';
import { LIGHT_THEME, DARK_THEME } from '../constants/themes';

export interface Theme {
  colors: {
    PRIMARY: string;
    SECONDARY: string;
    ACCENT: string;
    BACKGROUND: string;
    SURFACE: string;
    TEXT: string;
    TEXT_SECONDARY: string;
    ERROR: string;
    SUCCESS: string;
    WARNING: string;
    BORDER: string;
    LINK: string;
    BLACK: string;
    WHITE: string;
    // Golf-specific colors
    PAR: string;
    OVER_PAR: string;
    UNDER_PAR: string;
    EVEN: string;
    PAR_TEXT: string;
    UNDER_PAR_TEXT: string;
    OVER_PAR_TEXT: string;
    // UI state colors
    DISABLED: string;
    HOVER: string;
    FOCUS: string;
    // Input colors
    INPUT_BACKGROUND: string;
    INPUT_TEXT: string;
    INPUT_BORDER: string;
    INPUT_BORDER_FOCUS: string;
    // Button colors
    BUTTON_PRIMARY_BG: string;
    BUTTON_PRIMARY_TEXT: string;
    BUTTON_SECONDARY_BG: string;
    BUTTON_SECONDARY_TEXT: string;
    BUTTON_SUCCESS_BG: string;
    BUTTON_SUCCESS_TEXT: string;
    BUTTON_ERROR_BG: string;
    BUTTON_ERROR_TEXT: string;
    // Google/Apple button colors
    GOOGLE_BG: string;
    GOOGLE_TEXT: string;
    GOOGLE_BORDER: string;
    APPLE_BG: string;
    APPLE_TEXT: string;
    // Highlighted items
    HIGHLIGHTED_ITEM: string;
    // Navigation buttons
    NAVIGATION_BUTTON: string;
    // Status colors
    STATUS_ACTIVE: string;
    STATUS_COMPLETED: string;
    STATUS_CANCELLED: string;
  };
  spacing: typeof UI_CONSTANTS.SPACING;
  borderRadius: typeof UI_CONSTANTS.BORDER_RADIUS;
  transitions: typeof UI_CONSTANTS.TRANSITIONS;
  fonts: typeof UI_CONSTANTS.FONTS;
  fontSize: {
    BASE: string;
    SMALL: string;
    MEDIUM: string;
    LARGE: string;
    XLARGE: string;
  };
  isDark: boolean;
}

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  zoomLevel: number;
  setZoomLevel: (level: number) => void;
}

const createTheme = (isDark: boolean, zoomLevel: number): Theme => ({
  colors: isDark ? DARK_THEME.colors : LIGHT_THEME.colors,
  spacing: UI_CONSTANTS.SPACING,
  borderRadius: UI_CONSTANTS.BORDER_RADIUS,
  transitions: UI_CONSTANTS.TRANSITIONS,
  fonts: UI_CONSTANTS.FONTS,
  fontSize: {
    BASE: `${16 * zoomLevel}px`,
    SMALL: `${14 * zoomLevel}px`,
    MEDIUM: `${16 * zoomLevel}px`,
    LARGE: `${18 * zoomLevel}px`,
    XLARGE: `${24 * zoomLevel}px`,
  },
  isDark,
});

const ThemeContext = createContext<ThemeContextType | null>(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  const [zoomLevel, setZoomLevel] = useState(() => {
    const saved = localStorage.getItem('zoomLevel');
    return saved ? parseFloat(saved) : 1;
  });

  const theme = createTheme(isDarkMode, zoomLevel);

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
    localStorage.setItem('zoomLevel', zoomLevel.toString());

    // Update CSS variables
    Object.entries(theme.colors).forEach(([key, value]) => {
      document.documentElement.style.setProperty(
        `--color-${key.toLowerCase()}`,
        value
      );
    });

    // Update font size variables
    Object.entries(theme.fontSize).forEach(([key, value]) => {
      document.documentElement.style.setProperty(
        `--font-size-${key.toLowerCase()}`,
        value
      );
    });

    // Update body styles
    document.body.style.backgroundColor = theme.colors.BACKGROUND;
    document.body.style.color = theme.colors.TEXT;
    document.body.style.fontSize = theme.fontSize.BASE;
  }, [isDarkMode, zoomLevel, theme]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const handleSetZoomLevel = (level: number) => {
    // Clamp zoom level between 0.8 and 2.0
    const newZoom = Math.min(Math.max(level, 0.8), 2.0);
    setZoomLevel(newZoom);
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, zoomLevel, setZoomLevel: handleSetZoomLevel }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
